/**
 * Bulk grant: pick one event, multi-select users, grant (or revoke) access to
 * all of them in a single /admin/access call. For onboarding a group to a new
 * album quickly.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconCheck, IconSearch } from '@tabler/icons-react';
import { Modal } from '../ui/Modal.tsx';
import { Button } from '../ui/Button.tsx';
import { Spinner } from '../ui/Spinner.tsx';
import { useEvents } from '../../hooks/useEvents.ts';
import { adminListUsers, adminSetAccess } from '../../lib/apiClient.ts';
import { toast } from '../../state/toastStore.ts';
import { cn } from '../../lib/cn.ts';

interface BulkGrantDialogProps {
  open: boolean;
  onClose: () => void;
}

export function BulkGrantDialog({ open, onClose }: BulkGrantDialogProps) {
  const queryClient = useQueryClient();
  const { data: events } = useEvents();
  const [event, setEvent] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [userFilter, setUserFilter] = useState('');
  const [busy, setBusy] = useState(false);

  // Members only (admins implicitly have all events). One page of up to 200.
  const { data: usersPage, isLoading } = useQuery({
    queryKey: ['admin-users', 'bulk'],
    queryFn: ({ signal }) => adminListUsers({ limit: 200 }, signal),
    enabled: open,
  });

  const members = useMemo(
    () => (usersPage?.users ?? []).filter((u) => u.role !== 'admin'),
    [usersPage],
  );
  const filteredMembers = useMemo(() => {
    const q = userFilter.trim().toLowerCase();
    return members.filter(
      (u) => !q || u.email.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q),
    );
  }, [members, userFilter]);

  const eventNames = useMemo(
    () => (events ?? []).map((e) => e.name).sort((a, b) => a.localeCompare(b)),
    [events],
  );

  const toggleUser = (email: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(email)) n.delete(email);
      else n.add(email);
      return n;
    });

  const apply = async (granted: boolean) => {
    if (!event || selected.size === 0) return;
    setBusy(true);
    try {
      await adminSetAccess({ emails: [...selected], events: [event], granted });
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      if (granted) await queryClient.invalidateQueries({ queryKey: ['admin-requests'] });
      await Promise.all(
        [...selected].map((em) =>
          queryClient.invalidateQueries({ queryKey: ['admin-user', em] }),
        ),
      );
      toast.success(
        `${granted ? 'Granted' : 'Revoked'} "${event}" for ${selected.size} user${selected.size !== 1 ? 's' : ''}`,
      );
      setSelected(new Set());
      onClose();
    } catch {
      toast.error('Bulk update failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Grant an event to users">
      <div className="space-y-4">
        <div>
          <p className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted">
            Event
          </p>
          <select
            value={event}
            onChange={(e) => setEvent(e.target.value)}
            className="h-10 w-full rounded-xl border border-line bg-surface px-3 text-sm outline-none"
          >
            <option value="">Select an event…</option>
            {eventNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <div>
          <p className="mb-1 block text-xs font-medium uppercase tracking-wider text-muted">
            Users ({selected.size} selected)
          </p>
          <div className="relative mb-2">
            <IconSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              type="search"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder="Filter users…"
              aria-label="Filter users"
              className="h-9 w-full rounded-lg border border-line bg-surface pl-9 pr-3 text-sm outline-none placeholder:text-muted"
            />
          </div>
          <div className="max-h-56 overflow-y-auto rounded-xl border border-line">
            {isLoading ? (
              <div className="flex justify-center py-8 text-accent">
                <Spinner className="h-5 w-5" />
              </div>
            ) : filteredMembers.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted">No members.</p>
            ) : (
              <ul className="divide-y divide-line">
                {filteredMembers.map((u) => {
                  const on = selected.has(u.email);
                  return (
                    <li key={u.email}>
                      <button
                        onClick={() => toggleUser(u.email)}
                        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-grid"
                      >
                        <span
                          className={cn(
                            'grid h-5 w-5 shrink-0 place-items-center rounded border',
                            on ? 'border-accent bg-accent text-white' : 'border-line-2 text-transparent',
                          )}
                        >
                          <IconCheck className="h-3.5 w-3.5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-fg">{u.name || u.email}</span>
                          <span className="block truncate text-xs text-muted">{u.email}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => apply(false)} disabled={busy || !event || !selected.size}>
            Revoke
          </Button>
          <Button variant="primary" onClick={() => apply(true)} disabled={busy || !event || !selected.size}>
            {busy ? <Spinner className="h-4 w-4" /> : <IconCheck className="h-4 w-4" />}
            Grant access
          </Button>
        </div>
      </div>
    </Modal>
  );
}
